import { createReducer, on } from '@ngrx/store';
import * as ArchiveActions from '@states/archive/archive.actions';
import {
  AddArchiveModalConfig,
  ArchiveModel,
  ArchiveResponseFilter,
} from '@models/archive.model';
import { initialArchiveFilter } from '@consts/archive.conts';
import { SortDirection } from '@angular/material/sort';
import { Archive } from '@models/archive.model';
import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { Dictionary } from '@ngrx/entity/src/models';
import { OnRangeSelectedResult } from '@ui';

export interface ArchiveState extends EntityState<Archive.ArchiveDocument> {
  selectedArchive: ArchiveModel;
  orderBy: string;
  orderDirection: SortDirection;
  /**
   * @deprecated
   * use queryParams instead
   */
  filters: ArchiveResponseFilter;
  addArchiveModalConfig: AddArchiveModalConfig;
  isInitialLoaded: boolean;
  isLoading: boolean;
  downloadProgress: Dictionary<boolean>;
  notEmpty: boolean; //show no Data if result empty

  /**
   * Paging required
   */
  latestTs: number;
  isLastPage: boolean;

  queryParams: Archive.QueryParamFilters;
  dateRangeFilter: OnRangeSelectedResult;

}

export const archiveAdapter: EntityAdapter<Archive.ArchiveDocument> = createEntityAdapter<Archive.ArchiveDocument>({
  selectId: (archiveDocument: Archive.ArchiveDocument) => archiveDocument.sessionId,
});

const { selectAll } = archiveAdapter.getSelectors();

const initialState = archiveAdapter.getInitialState({
  selectedArchive: null,
  orderBy: 'name',
  orderDirection: 'asc',
  filters: initialArchiveFilter,
  addArchiveModalConfig: {
    disableSelectedCamera: false,
    disableSelectDateRange: false,
    hasSmartStorage: false,
    smartStorageInterval: null,
  },
  isLastPage: false,
  isInitialLoaded: true,
  downloadProgress: {},
  notEmpty: false,
  isLoading: true,

  latestTs: null,
  queryParams: Archive.defaultQueryParamFilters,
  dateRangeFilter: null,
});

export const archiveStateReducer = createReducer(
  initialState,
  on(ArchiveActions.resetToInitialState, state => {
    return {
      ...initialState,
    };
  }),
  on(ArchiveActions.resetSelectedArchive, state => {
    return {
      ...state,
      selectedArchive: initialState.selectedArchive,
    };
  }),
  on(ArchiveActions.changeGrantedAccessParams, (state, { property, value }) => {
    return {
      ...state,
      selectedArchive: {
        ...state.selectedArchive,
        grantedAccessParams: {
          [property]: value,
        },
      },
    };
  }),
  on(ArchiveActions.removeTags, (state, { value }) => {
    const index = state.selectedArchive.tags.findIndex(item => item === value);
    const tags = [...state.selectedArchive.tags];
    if (index > -1) {
      tags.splice(index, 1);
    }
    return {
      ...state,
      selectedArchive: {
        ...state.selectedArchive,
        tags,
      },
    };
  }),
  on(ArchiveActions.getArchiveListSuccess, (state, { archives }) => {
    const totalLength = archives.length;
    const totalArchives = Object.keys(state.entities);

    if (!totalLength) {
      return archiveAdapter.addMany(archives, {
        ...state,
        notEmpty: !!archives.length || !!totalArchives.length,
        isLastPage: archives.length < Archive.PAGE_LIMIT,
      });
    } else {
      return archiveAdapter.addMany(archives, {
        ...state,
        isLastPage: archives.length < Archive.PAGE_LIMIT,
        latestTs: archives[totalLength - 1].createdAt,
        notEmpty: !!archives.length || !!totalArchives.length,
      });
    }
  }),
  on(ArchiveActions.updateArchiveProgress, (state, { archive }) => {
    return archiveAdapter.updateOne({
        id: archive.sessionId,
        changes: {
          progress: archive.progress,
          status: archive.status,
        },
      },
      state);
  }),
  on(ArchiveActions.updateArchive, (state, { archive }) =>
    archiveAdapter.upsertOne(archive, state),
  ),
  on(ArchiveActions.addArchiveToCaseSuccess, (state, { sessionId, caseId }) => {
    return archiveAdapter.updateOne({
      id: sessionId,
      changes: { caseId },
    }, state);
  }),
  on(ArchiveActions.updateAddArchiveModalConfig, (state, { property, disable }) => {
    return {
      ...state,
      addArchiveModalConfig: {
        ...state.addArchiveModalConfig,
        [property]: disable,
      },
    };
  }),
  on(ArchiveActions.resetArchives, (state) => {
    return archiveAdapter.removeAll({
      ...state,
      isLastPage: initialState.isLastPage,
      latestTs: initialState.latestTs,
    });
  }),
  on(ArchiveActions.deleteArchiveSuccess, (state, { sessionId }) => {
    return archiveAdapter.removeOne(sessionId, {
      ...state,
      notEmpty: Object.keys(state.entities ?? {}).length > 1,
    });
  }),
  on(ArchiveActions.setIsLoading, (state, { isLoading }) => {
    return {
      ...state,
      isLoading,
    };
  }),
  on(ArchiveActions.setIsInitialLoading, (state, { isInitialLoading }) => {
    return {
      ...state,
      isInitialLoaded: isInitialLoading,
    };
  }),
  on(ArchiveActions.retrySuccess, (state, { archiveUpdate }) => {
    return archiveAdapter.updateOne({
      id: archiveUpdate.sessionId, changes: {
        progress: archiveUpdate.progress,
        status: archiveUpdate.status,
      },
    }, state);
  }),
  on(ArchiveActions.archiveSockedReceived, (state, { archive }) => {
    if (state.entities[archive.sessionId]) {
      return archiveAdapter.updateOne({
          id: archive.sessionId,
          changes: {
            progress: archive.progress,
            status: archive.status,
            _id: archive?._id,
            files: archive?.files,
          },
        },
        state);
    } else {
      return archiveAdapter.setAll([archive, ...selectAll(state)], { ...state, notEmpty: true });
    }
  }),
  on(ArchiveActions.downloadArchive, (state, { archive }) => {
    return {
      ...state,
      downloadProgress: {
        ...state.downloadProgress,
        [archive.sessionId]: true,
      },
    };
  }),
  on(ArchiveActions.downloadArchiveFinished, (state, { archive }) => {
    const downloadProgress = { ...state.downloadProgress };
    delete downloadProgress[archive.sessionId];
    return {
      ...state,
      downloadProgress,
    };
  }),
  on(ArchiveActions.setSelectedArchive, (state, { selectedArchive }) => {
    return {
      ...state,
      selectedArchive,
    };
  }),
  on(ArchiveActions.setCameraFilterSuccess, (state, { cameraIdsIncludes, cameraIdsExcludes }) => {
    return {
      ...state,
      isLoading: true,
      queryParams: {
        ...state.queryParams,
        cameraIdsIncludes,
        cameraIdsExcludes,
      },
    };
  }),
  on(ArchiveActions.setSearchQueryFilter, (state, { query }) => {
    return {
      ...state,
      isLoading: true,
      queryParams: {
        ...state.queryParams,
        query,
      },
    };
  }),
  on(ArchiveActions.setArchiveTypeFilter, (state, { types }) => {
    return {
      ...state,
      isLoading: true,
      queryParams: {
        ...state.queryParams,
        types,
      },
    };
  }),
  on(ArchiveActions.removeArchiveTypeFilter, (state, { typeName }) => {
    return {
      ...state,
      isLoading: true,
      queryParams: {
        ...state.queryParams,
        types: state.queryParams.types.filter(type => type !== typeName),
      },
    };
  }),
  on(ArchiveActions.setArchiveStatusFilter, (state, { statuses }) => {
    return {
      ...state,
      isLoading: true,
      queryParams: {
        ...state.queryParams,
        statuses,
      },
    };
  }),
  on(ArchiveActions.removeArchiveStatusFilter, (state, { status }) => {
    return {
      ...state,
      isLoading: true,
      queryParams: {
        ...state.queryParams,
        statuses: state.queryParams.statuses.filter(item => item !== status),
      },
    };
  }),
  on(ArchiveActions.setArchiveDateRangeFilter, (state, { dateRange }) => {
    return {
      ...state,
      isLoading: true,
      queryParams: {
        ...state.queryParams,
        dateRange,
      },
    };
  }),
  on(ArchiveActions.removeArchiveDateRangeFilter, (state) => {
    return {
      ...state,
      isLoading: true,
      queryParams: {
        ...state.queryParams,
        dateRange: Archive.defaultQueryParamFilters.dateRange,
      },
    };
  }),
);
